/**
 * How current these receivables are — the one line (or warning) above every
 * screen that shows them: Today, Customers, an account, Reports, and the
 * receivables data settings (docs/product/phase-0/01-FRESHNESS.md).
 *
 * The wording is `freshnessCopy`; who is told what follows the person's
 * capabilities. A change of state is said to a screen reader once, without the
 * age, from a live region that exists before anything changes; the visible
 * text is read in its place on the page.
 */
import { Link } from 'react-router-dom';
import { cx, IconAlert, IconInfo } from '@timely/ui';
import { holds } from '@timely/domain';
import { useActiveWorkspace } from '../workspace/WorkspaceProvider';
import { paths } from '../workspace/navigation';
import { useFormatters } from '../book/useFormatters';
import { useReceivablesFreshness } from './useReceivablesFreshness';
import { freshnessMessage, type FreshnessAudience, type FreshnessTone } from './freshnessCopy';

const BOX: Record<Exclude<FreshnessTone, 'quiet'>, string> = {
    notice: 'border-separator bg-card-2',
    warning: 'border-warn/25 bg-warn-bg',
    action: 'border-dang/25 bg-dang-bg',
};
const ICON: Record<Exclude<FreshnessTone, 'quiet'>, string> = { notice: 'text-label-3', warning: 'text-warn', action: 'text-dang' };
const HEADLINE: Record<Exclude<FreshnessTone, 'quiet'>, string> = {
    notice: 'text-label',
    warning: 'text-label',
    action: 'text-dang',
};
/** A word before the headline, so the strength is not carried by colour alone. */
const LEAD: Record<Exclude<FreshnessTone, 'quiet'>, string | null> = {
    notice: null,
    warning: 'Out of date:',
    action: 'Needs attention:',
};

export function SourceFreshness({ className, withLink = true, refreshKey }: {
    className?: string;
    /** False on the receivables data page itself, which is where the link goes. */
    withLink?: boolean;
    /** Changes when the page knows the figures just changed (a sync it ran). */
    refreshKey?: unknown;
}) {
    const context = useActiveWorkspace();
    const { workspace } = context;
    const { date } = useFormatters();
    const reading = useReceivablesFreshness(workspace.id, refreshKey);
    const audience: FreshnessAudience = holds(context, 'datasource.manage') ? 'manage'
        : holds(context, 'datasource.view') ? 'view' : 'member';
    const message = freshnessMessage(reading, audience, { day: date.long, time: date.time, dayKey: date.inputValue });

    return (
        <div className={className} data-freshness={message?.tone ?? 'none'}>
            <div role="status" className="sr-only">{message?.announce ?? ''}</div>
            {message && (message.tone === 'quiet' ? (
                <p className="inline-flex items-center gap-2 text-caption text-label-3">
                    {/* The settle mark, breathing while the figures are current. */}
                    <span aria-hidden="true" className="size-2 flex-none bg-mark animate-pulse-mark" />
                    <span><span className="sr-only">Receivables: </span>{message.headline}</span>
                </p>
            ) : (
                <section
                    aria-label="How current these receivables are"
                    className={cx('rounded-card border px-4 py-3 text-secondary text-label-2 flex gap-3', BOX[message.tone])}
                >
                    <span aria-hidden="true" className={cx('flex-none mt-px', ICON[message.tone])}>{message.tone === 'notice' ? <IconInfo size={18} /> : <IconAlert size={18} />}</span>
                    <div className="min-w-0">
                    <p className={cx('font-semibold', HEADLINE[message.tone])}>
                        {LEAD[message.tone] && <>{LEAD[message.tone]}{' '}</>}
                        {message.headline}
                    </p>
                    {message.detail && <p className="mt-0.5">{message.detail}</p>}
                    {(message.next || (withLink && message.linkToSource)) && (
                        <p className="mt-1.5 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                            {message.next && <span>{message.next}</span>}
                            {withLink && message.linkToSource && (
                                <Link to={paths(workspace.slug).setting('data')} className="font-semibold text-accent-text hover:underline whitespace-nowrap">
                                    Open receivables data
                                </Link>
                            )}
                        </p>
                    )}
                    </div>
                </section>
            ))}
        </div>
    );
}
